// 81 §8 — THE PANIC SWITCH.
//
// §8: "THE WHOLE FIRM — the panic switch, for a compromise whose extent is not
//  yet known." Reversible, immediate, in Settings.
//
// ── IT DOES NOT LOCK THE FIRM OUT, AND THE SCREEN SAYS WHY ─────────────────
//
// §8's floor — "two administrators must remain active at all times … without
// it, the switch is a way to lock a firm out of its own system and a rogue
// administrator reaches for it first" — means the switch cannot block
// administrators: blocking everyone would take the firm to zero and leave
// nobody able to reverse it. So it ENDS EVERY SESSION, administrators included,
// and BLOCKS everyone else. An administrator signs back in and is still an
// administrator.
//
// That is the single most important thing for the person about to press it to
// understand, so it is on the screen and not only in the code.
import { useState, useEffect } from "react";
import { useTranslation } from "../i18n";
import { FS } from "../lib/tokens";
import { getKillState, killFirm, reviveFirm } from "../api";
import type { KillState } from "../api";

type Toast = (message: string) => void;
type ApiFailure = { message?: string };

export default function FirmKillSwitchPanel({
  isAdmin,
  state,
  onChange,
  toast,
}: {
  isAdmin?: boolean;
  state?: KillState | null;
  onChange?: () => void;
  toast?: Toast;
}) {
  const { t, locale } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [armed, setArmed] = useState(false);
  const [local, setLocal] = useState<KillState | null>(state || null);

  // The host may pass the state down; when it does not, read it here. Mount
  // only, and inline — see AuthFactorsCard for the effect-deps reasoning.
  useEffect(() => {
    if (state) { setLocal(state); return; }
    let live = true;
    getKillState().then((s) => { if (live) setLocal(s); }).catch(() => { /* the panel simply shows nothing armed */ });
    return () => { live = false; };
  }, [state]);

  if (!isAdmin) return null;

  const on = !!local?.firm;

  const go = async (turnOn: boolean) => {
    setBusy(true); setErr("");
    try {
      if (turnOn) await killFirm(); else await reviveFirm();
      setArmed(false);
      const fresh = await getKillState();
      setLocal(fresh);
      onChange && onChange();
      toast && toast(turnOn
        ? t("kill.firm_on_toast", { fallback: "Every session has been ended. Everyone but the administrators is now locked out." })
        : t("kill.firm_off_toast", { fallback: "Sign-in is open again." }));
    } catch (e) {
      setErr(String((e as ApiFailure)?.message || "").trim()
        || t("kill.failed", { fallback: "That could not be done." }));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <p style={{ fontSize: FS.s11_5, color: "var(--ts)", margin: "6px 0 8px", lineHeight: 1.6 }}>
        {t("kill.firm_lede", {
          fallback:
            "For a compromise whose extent you do not yet know. It ends every session in the firm at once — including yours — and stops everyone except administrators from signing in again until you turn it off.",
        })}
      </p>
      <p style={{ fontSize: FS.s11, color: "var(--tm)", margin: "0 0 10px", lineHeight: 1.5 }}>
        {t("kill.firm_why_admins", {
          fallback:
            "Administrators are not locked out, on purpose: a switch that shut everybody out could be turned off by nobody. You will have to sign in again, and you will still be an administrator when you do.",
        })}
      </p>

      {err && <div style={{ fontSize: FS.s11, color: "var(--dg, #e5484d)", margin: "0 0 8px" }}>{err}</div>}

      {on ? (
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <span style={{ fontSize: FS.s11, color: "var(--rd)", fontWeight: 600 }}>
            {t("kill.firm_is_on", {
              d: local?.firm?.at ? new Date(local.firm.at).toLocaleString(locale) : "",
              fallback: `Sign-in is closed to everyone but administrators, since ${local?.firm?.at ? new Date(local.firm.at).toLocaleString(locale) : ""}.`,
            })}
          </span>
          <button type="button" className="btn btn-primary" disabled={busy} onClick={() => go(false)}>
            {t("kill.firm_off", { fallback: "Open sign-in again" })}
          </button>
        </div>
      ) : armed ? (
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <span style={{ fontSize: FS.s11, color: "var(--tm)" }}>
            {t("kill.firm_confirm", {
              fallback: "End every session in the firm and lock out everyone but administrators?",
            })}
          </span>
          <button type="button" className="btn btn-danger" disabled={busy} onClick={() => go(true)}>
            {t("common.confirm", { fallback: "Confirm" })}
          </button>
          <button type="button" className="btn btn-ghost" disabled={busy} onClick={() => setArmed(false)}>
            {t("common.cancel", { fallback: "Cancel" })}
          </button>
        </div>
      ) : (
        <button type="button" className="btn btn-danger" disabled={busy} onClick={() => setArmed(true)}>
          {t("kill.firm_on", { fallback: "Close sign-in for the whole firm" })}
        </button>
      )}
    </div>
  );
}
